/**
 * Las opciones del desplegable «qué trabajo le interesa», agrupadas.
 *
 * Existe para que los DOS sitios que lo preguntan —el alta del prospecto y el
 * panel de detalle del tablero— enseñen exactamente la misma lista. Cuando cada
 * uno recorría `serviceInterests` por su cuenta, cualquier cambio había que
 * hacerlo dos veces, y lo de abajo (el valor huérfano) no estaba en ninguno.
 *
 * Se exporta solo el interior del `<select>` y no un `<select>` entero: los dos
 * sitios traen su propia clase, su propio `id` y su propia opción vacía, y
 * envolverlos habría obligado a pasar todo eso como props para no ganar nada.
 */
import React from 'react';
import { IndustryConfig, OTHER_SERVICE_INTEREST } from '../../data/industryConfig';

interface Props {
    industryConfig: IndustryConfig;
    /** Lo que el prospecto tiene guardado AHORA. Se usa solo para no perderlo
     *  si ya no está en la lista; ver abajo. */
    value: string;
}

const ServiceInterestOptions: React.FC<Props> = ({ industryConfig, value }) => {
    /* El valor guardado que la lista ya no reconoce se enseña IGUAL, al final.
     *
     * `leads.service_interest` es texto libre y hay tres formas de acabar con un
     * valor que no está en el desplegable: el prospecto se apuntó cuando el
     * taller tenía otro oficio configurado, lo escribió la IA al transcribir una
     * llamada, o la lista de ese oficio cambió después.
     *
     * Sin esta opción el `<select>` no encuentra a qué opción casar y se pinta
     * VACÍO —como si el prospecto no tuviera interés apuntado—, y el siguiente
     * guardado del panel escribe esa cadena vacía encima del dato. El campo se
     * perdía sin que nadie tocara nada. */
    const conocidos = industryConfig.serviceInterests;
    const huerfano = value && !conocidos.includes(value) ? value : null;

    return (
        <>
            {industryConfig.serviceInterestGroups.map(grupo => (
                <optgroup key={grupo.label} label={grupo.label}>
                    {grupo.options.map(s => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </optgroup>
            ))}
            {huerfano && <option value={huerfano}>{huerfano}</option>}
            {/* 'Other' va suelto y el último, fuera de los grupos: es la salida
                cuando nada encaja, y dentro de un grupo habría que buscarlo. */}
            <option value={OTHER_SERVICE_INTEREST}>{OTHER_SERVICE_INTEREST}</option>
        </>
    );
};

export default ServiceInterestOptions;
